
import { useState, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import { faqData, faqCategories } from '../../mocks/faq';

export default function FAQPage() {
  const [openId, setOpenId] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const allCategories = ['All', ...faqCategories];

  const filtered = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        item.question.toLowerCase().includes(q) ||
        item.answer.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const toggle = (id: number) => setOpenId(openId === id ? null : id);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar transparent />

      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-teal-950">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/02c50c9535405f069d6e742909d50fd3.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-900/40 to-gray-950/80" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <AnimatedSection animation="fadeDown" delay={0}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-400 text-xs font-semibold tracking-widest uppercase mb-6">
              <i className="ri-question-answer-line"></i>
              Help Center
            </span>
          </AnimatedSection>
          <AnimatedSection animation="fadeUp" delay={100}>
            <h1 className="text-5xl font-extrabold text-white mb-5 leading-tight tracking-tight">
              Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">Questions</span>
            </h1>
          </AnimatedSection>
          <AnimatedSection animation="fadeUp" delay={200}>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
              Everything you need to know about building mobile apps with Your App Wizard — from getting started to publishing on the App Store and Google Play.
            </p>
          </AnimatedSection>
          {/* Search */}
          <AnimatedSection animation="scale" delay={300}>
            <div className="relative max-w-xl mx-auto">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                <i className="ri-search-line text-lg"></i>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search questions..."
                className="w-full pl-11 pr-5 py-3.5 rounded-xl bg-white/10 border border-white/15 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50 transition backdrop-blur-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-white transition cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Stats bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-wrap items-center justify-center gap-10">
          {[
            { icon: 'ri-question-line', value: '50', label: 'Questions Answered' },
            { icon: 'ri-folder-line', value: '8', label: 'Topic Categories' },
            { icon: 'ri-time-line', value: '< 2 min', label: 'Avg. Read Time' },
            { icon: 'ri-refresh-line', value: 'Weekly', label: 'Content Updates' },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                <i className={`${stat.icon} text-base`}></i>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* Sidebar Categories */}
            <aside className="lg:w-64 flex-shrink-0">
              <div className="sticky top-28">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 px-1">Browse by Topic</p>
                <nav className="space-y-1">
                  {allCategories.map((cat) => {
                    const count = cat === 'All' ? faqData.length : faqData.filter((f) => f.category === cat).length;
                    const isActive = activeCategory === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => { setActiveCategory(cat); setOpenId(null); }}
                        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          isActive
                            ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md shadow-teal-500/20'
                            : 'text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm'
                        }`}
                      >
                        <span>{cat}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </nav>

                {/* CTA Card */}
                <div className="mt-8 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-5 text-white">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl mb-3">
                    <i className="ri-customer-service-2-line text-xl"></i>
                  </div>
                  <p className="font-bold text-sm mb-1">Still have questions?</p>
                  <p className="text-white/80 text-xs mb-4 leading-relaxed">Our support team is ready to help you build your perfect app.</p>
                  <a
                    href="mailto:support@yourappwizard.com"
                    className="block text-center bg-white text-teal-600 font-semibold text-xs py-2.5 rounded-lg hover:bg-teal-50 transition cursor-pointer whitespace-nowrap"
                  >
                    Contact Support
                  </a>
                </div>
              </div>
            </aside>

            {/* FAQ List */}
            <div className="flex-1 min-w-0">
              {/* Result count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-500">
                  Showing <strong className="text-gray-800">{filtered.length}</strong> {filtered.length === 1 ? 'question' : 'questions'}
                  {activeCategory !== 'All' && <> in <strong className="text-teal-600">{activeCategory}</strong></>}
                  {searchQuery && <> matching <strong className="text-teal-600">"{searchQuery}"</strong></>}
                </p>
                {openId !== null && (
                  <button
                    onClick={() => setOpenId(null)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition cursor-pointer flex items-center gap-1 whitespace-nowrap"
                  >
                    <i className="ri-collapse-diagonal-line"></i>
                    Collapse all
                  </button>
                )}
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mx-auto mb-4">
                    <i className="ri-search-line text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-700 font-semibold mb-1">No results found</p>
                  <p className="text-gray-400 text-sm">Try a different search term or browse all categories.</p>
                  <button
                    onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                    className="mt-4 px-5 py-2 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition cursor-pointer whitespace-nowrap"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((item, idx) => {
                    const isOpen = openId === item.id;
                    return (
                      <AnimatedSection key={item.id} animation="fadeUp" delay={Math.min(idx * 30, 300)}>
                        <article
                          className={`bg-white rounded-xl border transition-all duration-300 overflow-hidden ${
                            isOpen
                              ? 'border-teal-300 shadow-lg shadow-teal-500/10'
                              : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                        >
                          <button
                            onClick={() => toggle(item.id)}
                            className="w-full flex items-start gap-4 px-6 py-5 text-left cursor-pointer group"
                            aria-expanded={isOpen}
                          >
                            <span className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold mt-0.5 transition-colors ${
                              isOpen ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-teal-50 group-hover:text-teal-600'
                            }`}>
                              {item.id}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap">
                                  {item.category}
                                </span>
                              </div>
                              <h4 className={`font-semibold text-sm leading-snug transition-colors ${isOpen ? 'text-teal-700' : 'text-gray-800 group-hover:text-teal-700'}`}>
                                <a id={`faq-${item.id}`}>{item.question}</a>
                              </h4>
                            </div>
                            <div className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                              isOpen ? 'bg-teal-500 text-white rotate-180' : 'bg-gray-100 text-gray-400 group-hover:bg-teal-50 group-hover:text-teal-500'
                            }`}>
                              <i className="ri-arrow-down-s-line text-base"></i>
                            </div>
                          </button>

                          {isOpen && (
                            <div className="px-6 pb-6">
                              <div className="ml-11 pl-4 border-l-2 border-teal-200">
                                <p className="text-gray-600 text-sm leading-relaxed">{item.answer}</p>
                              </div>
                            </div>
                          )}
                        </article>
                      </AnimatedSection>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <AnimatedSection animation="scale">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl mx-auto mb-6 shadow-lg shadow-teal-500/30">
              <i className="ri-rocket-2-line text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Ready to build your app?</h2>
            <p className="text-gray-500 text-base mb-8 leading-relaxed">
              Join thousands of entrepreneurs and businesses who have launched successful mobile apps with Your App Wizard — no coding required.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a
                href="/get-started"
                className="px-8 py-3.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-semibold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow-lg shadow-teal-500/30 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                Start Building Free
              </a>
              <a
                href="/pricing"
                className="px-8 py-3.5 border border-gray-200 text-gray-700 rounded-xl font-semibold text-sm hover:border-teal-400 hover:text-teal-600 transition-all cursor-pointer whitespace-nowrap"
              >
                View Pricing
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </>
  );
}
